'use client'

import { useState, useMemo, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { SmartHeader } from '@/widgets/smart-header'
import { Footer } from '@/widgets/footer'
import { CompanyCard } from '@/shared/ui/company-card/company-card'
import { CompanyModal } from '@/shared/ui/company-modal/company-modal'
import { Pagination } from '@/shared/ui/pagination/pagination'
import { Button } from '@/shared/ui/button/button'
import { Company } from '@/shared/types/company'
import { Search, SlidersHorizontal } from 'lucide-react'
import { getTaxSystemValue } from '@/shared/lib/tax-system'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
}

export const CompaniesPage = () => {
  const t = useTranslations('companies.catalog')
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCompanies, setTotalCompanies] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTaxSystem, setSelectedTaxSystem] = useState('all')
  const [priceRange, setPriceRange] = useState({ min: '', max: '' })
  const [sortBy, setSortBy] = useState('random')
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  // Загрузка компаний из API
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true)
        // Всегда используем API для карусели, чтобы знать какая компания новинка
        // Сортировку применяем на клиенте
        const url = '/api/companies?limit=100&carousel=true'
        const response = await fetch(url)
        if (response.ok) {
          const data = await response.json()
          setCompanies(data.companies || [])
          setTotalCompanies(data.companies?.length || 0)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Ошибка загрузки компаний:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCompanies()
  }, [sortBy])

  const handleCompanyClick = (company: Company) => {
    window.location.href = `/companies/${company.slug}`
  }

  const handleQuickView = (company: Company) => {
    setSelectedCompany(company)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedCompany(null)
  }

  const taxSystems = ['all', 'ОСНО', 'УСН 6%', 'УСН 15%', 'ЕСХН', 'ПСН', 'НПД', 'АУСН']

  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = companies

    // Поиск по названию и ОКВЭД
    if (searchQuery) {
      filtered = filtered.filter(
        (company) =>
          company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          company.okvedDescription.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    // Фильтр по налоговой системе
    if (selectedTaxSystem !== 'all') {
      filtered = filtered.filter((company) => {
        if (selectedTaxSystem === 'УСН 6%') {
          return company.taxSystem === 'USN' && company.usnRate === 6
        }
        if (selectedTaxSystem === 'УСН 15%') {
          return company.taxSystem === 'USN' && company.usnRate === 15
        }
        // Преобразуем русское название фильтра в английское для сравнения
        const systemValue = getTaxSystemValue(selectedTaxSystem)
        return company.taxSystem === systemValue
      })
    }

    // Фильтр по цене
    if (priceRange.min) {
      filtered = filtered.filter((company) => company.price >= parseInt(priceRange.min))
    }
    if (priceRange.max) {
      filtered = filtered.filter((company) => company.price <= parseInt(priceRange.max))
    }

    // Сортировка (пропускаем для рандомного порядка)
    if (sortBy !== 'random') {
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'priceAsc':
            return a.price - b.price
          case 'priceDesc':
            return b.price - a.price
          case 'nameAsc':
            return a.name.localeCompare(b.name)
          case 'nameDesc':
            return b.name.localeCompare(a.name)
          case 'dateDesc':
            return (
              new Date(b.registrationDate.split('.').reverse().join('-')).getTime() -
              new Date(a.registrationDate.split('.').reverse().join('-')).getTime()
            )
          case 'dateAsc':
            return (
              new Date(a.registrationDate.split('.').reverse().join('-')).getTime() -
              new Date(b.registrationDate.split('.').reverse().join('-')).getTime()
            )
          default:
            return 0
        }
      })
    }

    return filtered
  }, [companies, searchQuery, selectedTaxSystem, priceRange, sortBy])

  // Пагинация
  const totalPages = Math.ceil(filteredAndSortedCompanies.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const displayedCompanies = filteredAndSortedCompanies.slice(startIndex, startIndex + itemsPerPage)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Прокрутка к началу каталога при смене страницы
    const catalogSection = document.querySelector('#companies-catalog')
    if (catalogSection) {
      catalogSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Сброс на первую страницу при изменении фильтров
  const resetToFirstPage = () => {
    setCurrentPage(1)
  }

  return (
    <>
      <SmartHeader />
      <main className="min-h-screen bg-background pt-14">
        {/* Hero секция */}
        <section className="pt-24 pb-12 bg-gradient-to-b from-background via-background to-muted/30">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('title')}</h1>
              <p className="text-xl text-muted-foreground mb-6">{t('description')}</p>
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-8">
                <p className="text-center text-orange-700 dark:text-orange-300">
                  📞 Для уточнения актуальности и получения дополнительной информации о компаниях
                  свяжитесь с нашими менеджерами по телефону или в мессенджерах
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-sm text-muted-foreground justify-center">
                <div>Всего компаний: {totalCompanies}</div>
                <div className="hidden sm:block">•</div>
                <div>{t('totalFound', { count: filteredAndSortedCompanies.length })}</div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Фильтры и поиск */}
        <section className="py-6 border-b">
          <div className="container">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Поиск */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Поиск по названию или деятельности..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    resetToFirstPage()
                  }}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Кнопка фильтров на мобильных */}
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="md:hidden"
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Фильтры
                </Button>

                {/* Сортировка */}
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value)
                    resetToFirstPage()
                  }}
                  className="px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="random">{t('filters.sort.random')}</option>
                  <option value="priceAsc">{t('filters.sort.priceAsc')}</option>
                  <option value="priceDesc">{t('filters.sort.priceDesc')}</option>
                  <option value="nameAsc">{t('filters.sort.nameAsc')}</option>
                  <option value="nameDesc">{t('filters.sort.nameDesc')}</option>
                  <option value="dateDesc">{t('filters.sort.dateDesc')}</option>
                  <option value="dateAsc">{t('filters.sort.dateAsc')}</option>
                </select>
              </div>
            </div>

            {/* Фильтры */}
            <motion.div
              initial={false}
              animate={{ height: showFilters ? 'auto' : 0, opacity: showFilters ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className={`overflow-hidden md:!h-auto md:!opacity-100`}
            >
              <div className="pt-4 space-y-4 md:space-y-0 md:flex md:items-center md:gap-6">
                {/* Налоговая система */}
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('filters.taxSystem')}:
                  </label>
                  <select
                    value={selectedTaxSystem}
                    onChange={(e) => {
                      setSelectedTaxSystem(e.target.value)
                      resetToFirstPage()
                    }}
                    className="px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="all">{t('filters.all')}</option>
                    {taxSystems.slice(1).map((system) => (
                      <option key={system} value={system}>
                        {system}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Диапазон цен */}
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('filters.priceRange')}:
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="От"
                      value={priceRange.min}
                      onChange={(e) => {
                        setPriceRange((prev) => ({ ...prev, min: e.target.value }))
                        resetToFirstPage()
                      }}
                      className="w-24 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <span className="text-muted-foreground">—</span>
                    <input
                      type="number"
                      placeholder="До"
                      value={priceRange.max}
                      onChange={(e) => {
                        setPriceRange((prev) => ({ ...prev, max: e.target.value }))
                        resetToFirstPage()
                      }}
                      className="w-24 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>

                {/* Сброс фильтров */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('')
                    setSelectedTaxSystem('all')
                    setPriceRange({ min: '', max: '' })
                    setSortBy('random')
                    resetToFirstPage()
                  }}
                >
                  Сбросить
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Каталог компаний */}
        <section id="companies-catalog" className="py-12">
          <div className="container">
            {loading ? (
              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {Array.from({ length: itemsPerPage }).map((_, index) => (
                  <motion.div
                    key={`skeleton-${index}`}
                    variants={item}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="bg-card border rounded-lg p-6 animate-pulse">
                      <div className="h-6 bg-muted rounded mb-4"></div>
                      <div className="h-4 bg-muted rounded mb-2"></div>
                      <div className="h-4 bg-muted rounded mb-4 w-3/4"></div>
                      <div className="h-8 bg-muted rounded"></div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : displayedCompanies.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg text-muted-foreground">{t('noResults')}</p>
              </div>
            ) : (
              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {displayedCompanies.map((company) => {
                  // Определяем, является ли компания новинкой (последней добавленной в базу)
                  // Новинка должна быть одна и та же независимо от фильтров
                  // При использовании carousel=true, companies[1] - это всегда последняя добавленная
                  const isNewest = company.id === companies[1]?.id

                  return (
                    <motion.div
                      key={company.id}
                      variants={item}
                      transition={{ duration: 0.5 }}
                      className="relative"
                    >
                      {/* Индикатор "НОВИНКА" для последней добавленной компании */}
                      {isNewest && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3, duration: 0.5 }}
                          className="absolute -top-2 -right-2 z-20 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg"
                        >
                          НОВИНКА
                        </motion.div>
                      )}
                      <CompanyCard
                        company={company}
                        onDetailsClick={handleCompanyClick}
                        onQuickView={handleQuickView}
                        className={isNewest ? 'ring-2 ring-orange-500 shadow-xl relative z-10' : ''}
                      />
                    </motion.div>
                  )
                })}
              </motion.div>
            )}

            {/* Пагинация */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex justify-center mt-12"
              >
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </motion.div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {/* Модальное окно для быстрого просмотра */}
      <CompanyModal company={selectedCompany} isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  )
}
